/**
 * Funnel Build Service
 *
 * Builds funnel structures from bulk planner briefs and schedule data.
 * Converts content plans into Campaign -> AdSet -> Ad node hierarchies
 * compatible with FunnelBuilderContext.applyTemplate().
 */

// ============================================================================
// CONSTANTS
// ============================================================================

const STAGE_TO_OBJECTIVE = {
    TOFU: 'AWARENESS',
    MOFU: 'TRAFFIC',
    BOFU: 'CONVERSIONS'
};

const STAGE_POSITIONS = {
    TOFU: 100,
    MOFU: 500,
    BOFU: 900
};

const DEFAULT_BUDGET_PER_STAGE = {
    TOFU: 40,
    MOFU: 35,
    BOFU: 25
};

const STAGE_ORDER = ['TOFU', 'MOFU', 'BOFU'];

/**
 * Map social media platform names (from bulk planner) to ad platform names (funnel builder)
 */
const SOCIAL_TO_AD_PLATFORM = {
    instagram: 'meta',
    facebook: 'meta',
    tiktok: 'tiktok',
    linkedin: 'linkedin',
    youtube: 'google',
    twitter: null // Not supported in funnels
};

/**
 * Normalize human-readable CTA strings to funnel node format (UPPER_SNAKE_CASE)
 */
const CTA_MAP = {
    'learn more': 'LEARN_MORE',
    'discover': 'LEARN_MORE',
    'see how': 'LEARN_MORE',
    'find out': 'LEARN_MORE',
    'see how it works': 'LEARN_MORE',
    'shop now': 'SHOP_NOW',
    'buy now': 'SHOP_NOW',
    'sign up': 'SIGN_UP',
    'get started': 'SIGN_UP',
    'start free trial': 'SIGN_UP',
    'book now': 'BOOK_NOW',
    'get offer': 'GET_OFFER',
    'claim offer': 'GET_OFFER',
    'get quote': 'GET_QUOTE',
    'contact us': 'CONTACT_US',
    'download': 'DOWNLOAD',
    'subscribe': 'SUBSCRIBE',
    'watch more': 'WATCH_MORE',
    'compare': 'LEARN_MORE',
    'get demo': 'SIGN_UP',
    'watch video': 'WATCH_MORE'
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Normalize a CTA string from brief format to funnel node format
 * e.g. "Learn More" -> "LEARN_MORE"
 */
export function normalizeCta(ctaString) {
    if (!ctaString) return 'LEARN_MORE';

    const normalized = ctaString.toLowerCase().trim();

    // Check direct mapping
    if (CTA_MAP[normalized]) {
        return CTA_MAP[normalized];
    }

    // Check partial matches
    for (const [key, value] of Object.entries(CTA_MAP)) {
        if (normalized.includes(key) || key.includes(normalized)) {
            return value;
        }
    }

    // Fallback: convert to UPPER_SNAKE_CASE
    return ctaString.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z_]/g, '');
}

/**
 * Map social platform names to ad platform names
 * Returns null for unsupported platforms
 */
export function mapSocialToAdPlatform(socialPlatform) {
    if (!socialPlatform) return null;
    return SOCIAL_TO_AD_PLATFORM[socialPlatform.toLowerCase()] || null;
}

/**
 * Check if a brief's target platforms are compatible with a given ad platform
 */
function isBriefCompatibleWithPlatform(brief, adPlatform) {
    // If brief has no platform targeting, assume compatible with all
    if (!brief.platforms && !brief.targetPlatforms) return true;

    const briefPlatforms = brief.platforms || brief.targetPlatforms || [];
    if (briefPlatforms.length === 0) return true;

    return briefPlatforms.some(p => mapSocialToAdPlatform(p) === adPlatform);
}

/**
 * Get the earliest and latest dates from an array of scheduled posts
 */
function getDateRange(scheduledPosts) {
    if (!scheduledPosts || scheduledPosts.length === 0) return { start: null, end: null };

    let earliest = null;
    let latest = null;

    for (const post of scheduledPosts) {
        const postDate = post.date instanceof Date ? post.date : new Date(post.date);
        if (isNaN(postDate.getTime())) continue;

        if (!earliest || postDate < earliest) earliest = postDate;
        if (!latest || postDate > latest) latest = postDate;
    }

    return {
        start: earliest ? earliest.toISOString().split('T')[0] : null,
        end: latest ? latest.toISOString().split('T')[0] : null
    };
}

// ============================================================================
// MAIN BUILD FUNCTIONS
// ============================================================================

/**
 * Build a funnel structure from bulk planner briefs and schedule data.
 *
 * @param {Array} briefs - Array of generated briefs from bulk planner
 * @param {Array} scheduledPosts - Array of scheduled post objects from SmartScheduleStep
 * @param {string} platform - Target ad platform ('meta', 'google', 'tiktok', 'linkedin')
 * @param {Object} options - Configuration options
 * @param {Object} options.budgetPerStage - Budget per stage { TOFU, MOFU, BOFU }
 * @param {string} options.campaignStatus - Default campaign status
 * @returns {Object} Funnel structure { campaigns, adsets, ads, abtests, retargetingPools, stats }
 */
export function buildFunnelFromBriefs(briefs, scheduledPosts = [], platform, options = {}) {
    const budgetPerStage = options.budgetPerStage || DEFAULT_BUDGET_PER_STAGE;
    const campaignStatus = options.campaignStatus || 'pending';

    // Step 1: Filter to paid briefs with generated creatives
    const paidBriefs = briefs.filter(b => b.contentType === 'paid' && b.generatedCreativeId);

    // Filter by platform compatibility
    const compatibleBriefs = paidBriefs.filter(b => isBriefCompatibleWithPlatform(b, platform));

    // Build a map of briefId -> scheduled post for quick lookup
    const scheduleMap = new Map();
    for (const post of scheduledPosts) {
        if (post.briefId) {
            scheduleMap.set(post.briefId, post);
        }
    }

    // Step 2: Separate A/B test briefs from regular briefs
    const regularBriefs = [];
    const abTestGroups = new Map(); // testId -> [briefs]

    for (const brief of compatibleBriefs) {
        if (brief.abTest?.isAbTest && brief.abTest?.testId) {
            const testId = brief.abTest.testId;
            if (!abTestGroups.has(testId)) {
                abTestGroups.set(testId, []);
            }
            abTestGroups.get(testId).push(brief);
        } else {
            regularBriefs.push(brief);
        }
    }

    // Step 3: Group briefs by funnel stage
    const briefsByStage = { TOFU: [], MOFU: [], BOFU: [] };
    for (const brief of regularBriefs) {
        const stage = brief.funnelStage || 'TOFU';
        if (briefsByStage[stage]) {
            briefsByStage[stage].push(brief);
        }
    }

    // Also assign A/B test groups to stages
    const abTestsByStage = { TOFU: [], MOFU: [], BOFU: [] };
    for (const [testId, testBriefs] of abTestGroups) {
        const stage = testBriefs[0]?.funnelStage || 'TOFU';
        if (abTestsByStage[stage]) {
            abTestsByStage[stage].push({ testId, briefs: testBriefs });
        }
    }

    // Step 4: Build the structure
    const campaigns = [];
    const adsets = [];
    const ads = [];
    const abtests = [];
    const retargetingPools = [];

    const activeStages = [];

    for (const stage of STAGE_ORDER) {
        const stageBriefs = briefsByStage[stage];
        const stageAbTests = abTestsByStage[stage];

        if (stageBriefs.length === 0 && stageAbTests.length === 0) continue;

        activeStages.push(stage);

        // Collect all briefs for this stage (for schedule calculation)
        const allStageBriefs = [
            ...stageBriefs,
            ...stageAbTests.flatMap(t => t.briefs)
        ];

        // Calculate schedule from scheduled posts
        const stageScheduledPosts = allStageBriefs
            .map(b => scheduleMap.get(b.id))
            .filter(Boolean);
        const dateRange = getDateRange(stageScheduledPosts);

        // Create Campaign node
        const campaign = {
            type: 'campaign',
            data: {
                name: `${stage === 'TOFU' ? 'Awareness' : stage === 'MOFU' ? 'Consideration' : 'Conversion'} Campaign`,
                objective: STAGE_TO_OBJECTIVE[stage],
                platform,
                budget: { amount: budgetPerStage[stage] || DEFAULT_BUDGET_PER_STAGE[stage], type: 'daily' },
                funnelStage: stage,
                status: campaignStatus,
                schedule: {
                    start: dateRange.start || '',
                    end: dateRange.end || ''
                }
            },
            _stage: stage,
            _pillarIds: new Set()
        };
        campaigns.push(campaign);

        // Group briefs by pillar within stage -> AdSets
        const briefsByPillar = new Map();
        for (const brief of stageBriefs) {
            const pillarKey = brief.pillarId || 'default';
            if (!briefsByPillar.has(pillarKey)) {
                briefsByPillar.set(pillarKey, {
                    pillarId: brief.pillarId,
                    pillarName: brief.pillarName || 'General',
                    briefs: []
                });
            }
            briefsByPillar.get(pillarKey).briefs.push(brief);
        }

        // Also group A/B test briefs by pillar
        for (const testGroup of stageAbTests) {
            const firstBrief = testGroup.briefs[0];
            const pillarKey = firstBrief?.pillarId || 'default';
            if (!briefsByPillar.has(pillarKey)) {
                briefsByPillar.set(pillarKey, {
                    pillarId: firstBrief?.pillarId,
                    pillarName: firstBrief?.pillarName || 'General',
                    briefs: [],
                    abTests: []
                });
            }
            const pillarGroup = briefsByPillar.get(pillarKey);
            if (!pillarGroup.abTests) pillarGroup.abTests = [];
            pillarGroup.abTests.push(testGroup);
        }

        // Create AdSet nodes per pillar group
        for (const [, pillarGroup] of briefsByPillar) {
            campaign._pillarIds.add(pillarGroup.pillarId);

            // Calculate adset schedule from its briefs
            const adsetBriefs = [
                ...pillarGroup.briefs,
                ...(pillarGroup.abTests || []).flatMap(t => t.briefs)
            ];
            const adsetScheduledPosts = adsetBriefs
                .map(b => scheduleMap.get(b.id))
                .filter(Boolean);
            const adsetDateRange = getDateRange(adsetScheduledPosts);

            const adset = {
                type: 'adset',
                data: {
                    name: `${pillarGroup.pillarName} Audience`,
                    funnelStage: stage,
                    targeting: {
                        ageMin: 18,
                        ageMax: 65,
                        genders: [],
                        locations: [],
                        interests: []
                    },
                    schedule: {
                        start: adsetDateRange.start || '',
                        end: adsetDateRange.end || ''
                    },
                    status: 'draft'
                },
                _campaignIndex: campaigns.length - 1,
                _stage: stage
            };
            adsets.push(adset);

            // Create Ad nodes for regular briefs
            for (const brief of pillarGroup.briefs) {
                const scheduledPost = scheduleMap.get(brief.id);
                const ad = buildAdNodeFromBrief(brief, scheduledPost);
                ad._adsetIndex = adsets.length - 1;
                ad._stage = stage;
                ads.push(ad);
            }

            // Create ABTest + Ad nodes for A/B test groups
            for (const testGroup of (pillarGroup.abTests || [])) {
                const abtest = {
                    type: 'abtest',
                    data: {
                        name: `A/B Test: ${testGroup.briefs[0]?.abTest?.testElement || 'Creative'}`,
                        trafficSplit: testGroup.briefs.length === 2 ? [50, 50] : Array(testGroup.briefs.length).fill(Math.floor(100 / testGroup.briefs.length)),
                        winnerCriteria: testGroup.briefs[0]?.abTest?.testElement === 'headline' ? 'CTR' : 'CTR',
                        duration: 14,
                        autoPauseLoser: true,
                        variantIds: [],
                        status: 'draft'
                    },
                    _adsetIndex: adsets.length - 1,
                    _stage: stage
                };
                abtests.push(abtest);

                // Create variant ads
                const variantLabels = ['A', 'B', 'C', 'D'];
                for (let i = 0; i < testGroup.briefs.length; i++) {
                    const brief = testGroup.briefs[i];
                    const scheduledPost = scheduleMap.get(brief.id);
                    const ad = buildAdNodeFromBrief(brief, scheduledPost);
                    ad.data.variant = variantLabels[i] || String.fromCharCode(65 + i);
                    ad.data.name = `${ad.data.name} (Variant ${ad.data.variant})`;
                    ad._abtestIndex = abtests.length - 1;
                    ad._stage = stage;
                    ads.push(ad);
                }
            }
        }

        // Set the primary pillar on the campaign node data
        // Use the first pillar group's data (most common pillar for the stage)
        const firstPillarGroup = Array.from(briefsByPillar.values())[0];
        if (firstPillarGroup?.pillarId) {
            campaign.data.pillarId = firstPillarGroup.pillarId;
            campaign.data.pillarName = firstPillarGroup.pillarName;
            // Get pillar color from the first brief that has it
            const firstBriefWithColor = [...(firstPillarGroup.briefs || []), ...(firstPillarGroup.abTests || []).flatMap(t => t.briefs)]
                .find(b => b.pillarColor);
            if (firstBriefWithColor) {
                campaign.data.pillarColor = firstBriefWithColor.pillarColor;
            }
        }
    }

    // Step 5: Create RetargetingPools between adjacent active stages
    for (let i = 0; i < activeStages.length - 1; i++) {
        const sourceStage = activeStages[i];
        const targetStage = activeStages[i + 1];

        retargetingPools.push({
            type: 'retargetingPool',
            data: {
                name: `${sourceStage} → ${targetStage} Retargeting`,
                poolType: sourceStage === 'TOFU' ? 'engagement' : 'website',
                sourceStage,
                targetStage,
                sourceCampaigns: [],
                trigger: {
                    type: sourceStage === 'TOFU' ? 'engagement' : 'website_visit',
                    threshold: 1,
                    lookbackDays: 30
                },
                estimatedSize: 0,
                isActive: true,
                status: 'draft'
            },
            _sourceStage: sourceStage,
            _targetStage: targetStage
        });
    }

    // Build pillar mapping for campaign nodes
    const pillarMapping = {};
    campaigns.forEach((campaign, idx) => {
        const pillarIds = Array.from(campaign._pillarIds);
        if (pillarIds.length > 0 && pillarIds[0]) {
            pillarMapping[idx] = {
                pillarId: pillarIds[0],
                // We'll include all pillar IDs in case there are multiple
                allPillarIds: pillarIds
            };
        }
    });

    // Stats for display
    const stats = {
        totalPaidBriefs: paidBriefs.length,
        compatibleBriefs: compatibleBriefs.length,
        excludedBriefs: paidBriefs.length - compatibleBriefs.length,
        organicBriefs: briefs.filter(b => b.contentType !== 'paid').length,
        campaignCount: campaigns.length,
        adsetCount: adsets.length,
        adCount: ads.length,
        abtestCount: abtests.length,
        retargetingPoolCount: retargetingPools.length,
        stages: activeStages
    };

    return {
        campaigns,
        adsets,
        ads,
        abtests,
        retargetingPools,
        pillarMapping,
        stats
    };
}

/**
 * Build an Ad node data object from a brief and optional scheduled post
 */
function buildAdNodeFromBrief(brief, scheduledPost) {
    return {
        type: 'ad',
        data: {
            name: brief.selectedHeadline || brief.marketingAngle || 'Generated Ad',
            creativeId: brief.generatedCreativeId || null,
            creativeUrl: brief.generatedImageUrl || null,
            creativeThumbnail: brief.generatedThumbnailUrl || null,
            isVideo: brief.outputType === 'video',
            headline: brief.selectedHeadline || '',
            primaryText: brief.caption || '',
            cta: normalizeCta(brief.cta),
            status: 'generated',
            funnelStage: brief.funnelStage || 'TOFU',
            // Schedule data from the smart scheduler
            scheduledDate: scheduledPost?.date
                ? (scheduledPost.date instanceof Date ? scheduledPost.date.toISOString().split('T')[0] : String(scheduledPost.date).split('T')[0])
                : null,
            scheduledTime: scheduledPost?.time || null,
            // Brief reference for traceability
            briefId: brief.id || null,
            outputType: brief.outputType || 'image'
        }
    };
}

/**
 * Convert the funnel structure into a template format compatible with
 * FunnelBuilderContext.applyTemplate()
 *
 * @param {Object} funnelStructure - Output from buildFunnelFromBriefs()
 * @returns {Object} Template { nodes[], edges[] } ready for applyTemplate()
 */
export function buildFunnelTemplate(funnelStructure) {
    const { campaigns, adsets, ads, abtests, retargetingPools } = funnelStructure;
    const nodes = [];
    const edges = [];

    // Index tracking - maps original array positions to node array positions
    const campaignIndexMap = new Map(); // campaign array idx -> nodes array idx
    const adsetIndexMap = new Map();
    const abtestIndexMap = new Map();

    // Add Campaign nodes
    campaigns.forEach((campaign, i) => {
        const nodeIdx = nodes.length;
        campaignIndexMap.set(i, nodeIdx);
        nodes.push({
            type: 'campaign',
            data: { ...campaign.data },
            position: { x: STAGE_POSITIONS[campaign._stage] || 100, y: 100 }
        });
    });

    // Add RetargetingPool nodes and connect to source campaigns
    retargetingPools.forEach((pool) => {
        const nodeIdx = nodes.length;
        nodes.push({
            type: 'retargetingPool',
            data: { ...pool.data },
            position: {
                x: (STAGE_POSITIONS[pool._sourceStage] + STAGE_POSITIONS[pool._targetStage]) / 2,
                y: 50
            }
        });

        // Connect source campaign -> retargeting pool
        const sourceCampaignIdx = campaigns.findIndex(c => c._stage === pool._sourceStage);
        if (sourceCampaignIdx !== -1) {
            edges.push({
                sourceIndex: campaignIndexMap.get(sourceCampaignIdx),
                targetIndex: nodeIdx,
                sourceHandle: 'retarget-out',
                targetHandle: 'pool-in'
            });
        }
    });

    // Add AdSet nodes and connect to campaigns
    adsets.forEach((adset, i) => {
        const nodeIdx = nodes.length;
        adsetIndexMap.set(i, nodeIdx);
        nodes.push({
            type: 'adset',
            data: { ...adset.data },
            position: {
                x: STAGE_POSITIONS[adset._stage] || 100,
                y: 300
            }
        });

        // Edge: Campaign -> AdSet
        const campaignNodeIdx = campaignIndexMap.get(adset._campaignIndex);
        if (campaignNodeIdx !== undefined) {
            edges.push({
                sourceIndex: campaignNodeIdx,
                targetIndex: nodeIdx,
                sourceHandle: 'adset-out',
                targetHandle: 'campaign-in'
            });
        }
    });

    // Add ABTest nodes and connect to adsets
    abtests.forEach((abtest, i) => {
        const nodeIdx = nodes.length;
        abtestIndexMap.set(i, nodeIdx);
        nodes.push({
            type: 'abtest',
            data: { ...abtest.data },
            position: {
                x: STAGE_POSITIONS[abtest._stage] || 100,
                y: 460
            }
        });

        // Edge: AdSet -> ABTest
        const adsetNodeIdx = adsetIndexMap.get(abtest._adsetIndex);
        if (adsetNodeIdx !== undefined) {
            edges.push({
                sourceIndex: adsetNodeIdx,
                targetIndex: nodeIdx,
                sourceHandle: 'ad-out',
                targetHandle: 'adset-in'
            });
        }
    });

    // Add Ad nodes and connect to adsets or abtests
    ads.forEach((ad) => {
        const nodeIdx = nodes.length;
        nodes.push({
            type: 'ad',
            data: { ...ad.data },
            position: {
                x: STAGE_POSITIONS[ad._stage] || 100,
                y: 620
            }
        });

        if (ad._abtestIndex !== undefined) {
            // Edge: ABTest -> Ad (variant)
            const abtestNodeIdx = abtestIndexMap.get(ad._abtestIndex);
            if (abtestNodeIdx !== undefined) {
                edges.push({
                    sourceIndex: abtestNodeIdx,
                    targetIndex: nodeIdx,
                    sourceHandle: 'variant-out',
                    targetHandle: 'adset-in'
                });
            }
        } else if (ad._adsetIndex !== undefined) {
            // Edge: AdSet -> Ad
            const adsetNodeIdx = adsetIndexMap.get(ad._adsetIndex);
            if (adsetNodeIdx !== undefined) {
                edges.push({
                    sourceIndex: adsetNodeIdx,
                    targetIndex: nodeIdx,
                    sourceHandle: 'ad-out',
                    targetHandle: 'adset-in'
                });
            }
        }
    });

    return { nodes, edges };
}

/**
 * Get statistics about how briefs would be distributed across a funnel
 * for display in the DeployToFunnelModal before creation.
 *
 * @param {Array} briefs - All briefs from bulk planner
 * @param {string} platform - Target ad platform
 * @returns {Object} Preview stats
 */
export function getFunnelPreviewStats(briefs, platform) {
    const paidBriefs = briefs.filter(b => b.contentType === 'paid');
    const paidWithCreatives = paidBriefs.filter(b => b.generatedCreativeId);
    const compatible = paidWithCreatives.filter(b => isBriefCompatibleWithPlatform(b, platform));
    const organicCount = briefs.filter(b => b.contentType !== 'paid').length;

    const byStage = { TOFU: 0, MOFU: 0, BOFU: 0 };
    for (const brief of compatible) {
        const stage = brief.funnelStage || 'TOFU';
        if (byStage[stage] !== undefined) byStage[stage]++;
    }

    return {
        totalBriefs: briefs.length,
        paidBriefs: paidBriefs.length,
        paidWithCreatives: paidWithCreatives.length,
        compatibleBriefs: compatible.length,
        organicBriefs: organicCount,
        byStage,
        hasContent: compatible.length > 0
    };
}
